import { logger } from '@sentry/gatsby';
import { type PersonalizationAttributes } from './component-personalization.schema';
import z from 'zod/v4';

interface PersonalizationValidationArgs {
  condition: Queries.PERSONALIZATION_CONDITIONFragment;
  attributes: PersonalizationAttributes;
}

const getMetricsForCondition = (
  condition: Queries.PERSONALIZATION_CONDITIONFragment,
  attributes: PersonalizationAttributes
) => {
  switch (condition.elements.metric_to_compare.value) {
    case 'buying_stage':
      return attributes.scores.map((score) => score.buying_stage);
    case 'company_size':
      return [attributes.company.employee_count];
    case 'industry_naics':
      return [attributes.company.naics];
    case 'industry_name':
      return [
        attributes.company.industry,
        ...(attributes.company.industry_v2 ?? []).map((i) => i.industry),
      ];
    case 'industry_sic':
      return [attributes.company.sic];
    case 'location_country':
      return [attributes.company.country_iso_code];
    case 'location_region':
      return [attributes.company.region];
    case 'revenue':
      return [attributes.company.annual_revenue];
  }
};

const prepareForNumberComparison = (
  value: unknown,
  operator: Queries.kontent_item_personalization_condition_elements_operator_enum
) => {
  const numberSchema = z.string().trim().pipe(z.coerce.number());

  const commaSeparatedNumbersSchema = z.preprocess((value) => {
    if (typeof value === 'string') {
      return value.split(',');
    }

    return [value];
  }, z.array(numberSchema));

  const result =
    operator === 'in'
      ? commaSeparatedNumbersSchema.safeParse(value)
      : numberSchema.safeParse(value);

  if (result.success) {
    return result.data;
  } else {
    return null;
  }
};

const compareNumberMetric = (
  metric: number,
  operator: Queries.kontent_item_personalization_condition_elements_operator_enum,
  dataValue: string
) => {
  const value = prepareForNumberComparison(dataValue, operator);
  if (value === null) {
    logger.error(
      'Component Personalization - Number comparison - Could not convert value to number',
      {
        dataValue,
        operator,
      }
    );
    return false;
  }

  logger.info('Component Personalization - Number comparison - performing check', {
    value,
    operator,
    metric,
  });
  switch (operator) {
    case 'in': {
      return Array.isArray(value) && value.includes(metric);
    }
    case 'equals': {
      return metric === value;
    }
    case 'greater_than': {
      return !Array.isArray(value) && metric > value;
    }
    case 'lower_than': {
      return !Array.isArray(value) && metric < value;
    }
    case 'greater_than_or_equal': {
      return !Array.isArray(value) && metric >= value;
    }
    case 'lower_than_or_equal': {
      return !Array.isArray(value) && metric <= value;
    }
    default:
      logger.error('Component Personalization - Number comparison - Unsupported operator', {
        value,
        operator,
        metric,
      });
      return false;
  }
};

const compareStringMetric = (
  metric: string,
  operator: Queries.kontent_item_personalization_condition_elements_operator_enum,
  dataValue: string
) => {
  const value = operator === 'in' ? dataValue.split(',').map((v) => v.trim()) : dataValue.trim();

  logger.info('Component Personalization - String comparison - performing check', {
    value,
    operator,
    metric,
  });
  switch (operator) {
    case 'in': {
      return value.includes(metric);
    }
    case 'contains': {
      return !Array.isArray(value) && metric.includes(value);
    }
    case 'equals': {
      return metric === value;
    }
    default:
      logger.error('Component Personalization - String comparison - Unsupported operator', {
        value,
        operator,
        metric,
      });
      return false;
  }
};

export const personalizationConditionMatches = ({
  condition,
  attributes,
}: PersonalizationValidationArgs) => {
  const metrics = getMetricsForCondition(condition, attributes);
  const operator = condition.elements.operator.value;
  const conditionId = condition.system.id;

  return metrics.some((metric) => {
    if (typeof metric === 'undefined') {
      logger.error('Component Personalization - Invalid metric for condition', {
        conditionId,
      });
      return false;
    }

    if (typeof metric === 'number') {
      return compareNumberMetric(metric, operator, condition.elements.data_value.value);
    }

    if (typeof metric === 'string') {
      return compareStringMetric(metric, operator, condition.elements.data_value.value);
    }

    logger.error('Component Personalization - Invalid metric type', {
      conditionId,
      metric,
    });

    return false;
  });
};
